import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Drawer,
  Button,
  Box,
  Typography,
  TextField,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import SetupPayment from "../../payment/assets/setupPayment.png";
import "./settingsAppointmentTypes.css";
import copyIcon from "./assets/copyIcon.png";
import { height, styled } from "@mui/system";
import AddIcon from "./assets/addIcon.png";
import NarrowDropdown2 from "../../../components/input/new/narrowDropdown2";
import NarrowInputField from "../../../components/input/new/narrowInputField";
import BackIcon from "./assets/backIcon.png";
import TextAreaWithCount from "../../../components/input/new/textAreaWithCount";
import AIIcon from "./assets/ai_icon.png";
import UpIcon from "./assets/up_icon.png";
import { useNavigate, useLocation } from "react-router-dom";
import UploadForm from "./uploadForm";
import { authenticatedFetch } from "../../../network/api";

const policy = [
  { label: "Anytime", value: "0" },
  { label: "24 Hours", value: "24" },
  { label: "48 Hours", value: "48" },
  { label: "72 Hours", value: "72" },
];

const duration = [
  { label: "30 Minutes", value: "30" },
  { label: "60 Minutes", value: "60" },
  { label: "90 Minutes", value: "90" },
];

const CustomSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#144A8C" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#144A8C",
  },
}));

// Appointment Card Component
const AppointmentCard = ({ appointment, onMenuClick }) => (
  <div className="appt-appointment-card">
    <div className="appt-appointment-card-header">
      <p className="appt-card-title">{appointment.appointment_name}</p>
      <MoreHoriz
        className="menu-icon"
        onClick={(e) => onMenuClick(e, appointment)}
      />
    </div>

    <p className="appt-appointment-duration">
      {appointment.appointment_duration} mins ·{" "}
      <span className="price">
        ${(appointment.appointment_price / 100).toFixed(2)}
      </span>
    </p>

    <div className="appt-description-container">
      <p className="appt-appointment-description">
        {appointment.appointment_description}
      </p>
    </div>

    <a href="#" className="appt-view-link">
      View Booking Detail
    </a>

    <hr className="appt-divider" />

    <div className="appt-copy-link">
      <img src={copyIcon} alt="Copy" />
      <p className="appt-copy-link-text">Copy Link</p>
    </div>
  </div>
);

const SettingsAppointmentTypes = () => {
  // Redux
  const userId = useSelector(
    (state) => state.attorneyData?.attorneyData?.user_id
  );
  const firmId = useSelector(
    (state) => state.attorneyData?.attorneyData?.firm_id
  );

  const lawyer_image = useSelector(
    (state) => state.attorneyData?.attorneyData?.profile_photo_url
  );
  const navigate = useNavigate();
  const location = useLocation();
  // Sidebar state
  const [professionals, setProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [inviteError, setInviteError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  // Appointment types for selected professional
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  // Stripe & drawer state (unchanged)
  const [firmStripeStatus, setFirmStripeStatus] = useState(null);
  const [stripeAccountId, setStripeAccountId] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [intakeForms, setIntakeForms] = useState([]);
  // which form the user picks
  const [selectedIntakeForm, setSelectedIntakeForm] = useState("");
  const [newAppointment, setNewAppointment] = useState({
    appointment_name: "",
    appointment_duration: "",
    appointment_description: "",
    appointment_price: "",
    appointment_currency: "",
    appointment_max_capacity: "",
    appointment_time_zone: "",
    appointment_notes: "",
    first_consultation_free: false,
    appointment_status: "active",
    appointment_type: "",
    lawyerPhoto: lawyer_image,
  });
  const [showUploadForm, setShowUploadForm] = useState(false);

  // 1) Fetch firm members once
  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        const res = await fetch(
          `/api/attorney/fetchFirmMembers?firm_id=${firmId}`
        );
        const data = await res.json();
        if (res.ok) {
          setProfessionals(data.lawyers);
          setFilteredProfessionals(data.lawyers);
          const me = data.lawyers.find((l) => l.user_id === userId);
          setSelectedProfessional(me || data.lawyers[0]);
        } else {
          setInviteError("Failed to load professionals.");
        }
      } catch {
        setInviteError("Failed to load professionals.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfessionals();
  }, [firmId, userId]);

  useEffect(() => {
    if (!selectedProfessional) return;
    authenticatedFetch(
      `/api/attorney/webapp/intake/forms/list?lawyerId=${selectedProfessional.user_id}`
    )
      .then((res) => res.json())
      .then((json) => setIntakeForms(json.forms || []))
      .catch(console.error);
  }, [selectedProfessional]);

  // 2) Filter sidebar list
  useEffect(() => {
    if (!searchTerm) {
      setFilteredProfessionals(professionals);
    } else {
      setFilteredProfessionals(
        professionals.filter((p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, professionals]);

  // 3) Fetch appointment types when selection changes
  useEffect(() => {
    if (!selectedProfessional) return;
    const fetchAppointmentTypes = async () => {
      setLoadingAppointments(true);
      try {
        const res = await authenticatedFetch(
          `/api/attorney/webapp/fetchAppointmentTypes?user_id=${selectedProfessional.user_id}`
        );
        const data = await res.json();
        if (res.ok) {
          setAppointmentTypes(data.appointmentTypes);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingAppointments(false);
      }
    };
    fetchAppointmentTypes();
  }, [selectedProfessional]);

  // 4) Check Stripe status (unchanged)
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await authenticatedFetch("/api/attorney/webapp/checkFirmStripeStatus", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const json = await res.json();
        setFirmStripeStatus(res.ok && json.result);
        setStripeAccountId(json.stripe_account_id || "");
      } catch {
        setFirmStripeStatus(false);
      }
    })();
  }, [userId]);

  useEffect(() => {
    if (location.state?.newFormId) {
      // Add the new form to intakeForms if not present
      if (!intakeForms.some((f) => f.id === location.state.newFormId)) {
        setIntakeForms((prev) => [
          ...prev,
          {
            id: location.state.newFormId,
            name: location.state.newFormName || "New Form",
          },
        ]);
      }
      setSelectedIntakeForm(location.state.newFormId);
      // Restore the rest of the draft
      if (location.state.appointmentDraft) {
        setNewAppointment(location.state.appointmentDraft);
      }
      // Optionally clear the state so it doesn't re-trigger
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location]);

  // Drawer, menu & form handlers (unchanged) …
  const handleMenuClick = (e, appt) => {
    setMenuAnchor(e.currentTarget);
    setCurrentAppointment(appt);
  };
  const handleMenuClose = () => setMenuAnchor(null);
  const handleCancelPressed = () => {
    setIsAdding(false);
    setNewAppointment({
      appointment_name: "",
      appointment_duration: "",
      appointment_description: "",
      appointment_price: "",
      appointment_currency: "",
      appointment_max_capacity: "",
      appointment_time_zone: "",
      appointment_notes: "",
      first_consultation_free: false,
      appointment_status: "active",
      appointment_type: "",
      lawyerPhoto: lawyer_image,
    });
  };
  const handleInputChange = (e) =>
    setNewAppointment({ ...newAppointment, [e.target.name]: e.target.value });
  const handleCreateAppointment = async () => {
    if (!stripeAccountId) return;
    try {
      const res = await authenticatedFetch("/api/attorney/webapp/createAppointmentType", {
        method: "POST",
        body: JSON.stringify({
          ...newAppointment,
          user_id: userId,
          stripe_connect_id: stripeAccountId,
          intake_form_id: selectedIntakeForm,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setIsAdding(false);
        setAppointmentTypes((prev) => [...prev, data]);
        setNewAppointment({
          appointment_name: "",
          appointment_duration: "",
          appointment_description: "",
          appointment_price: "",
          appointment_currency: "",
          appointment_max_capacity: "",
          appointment_time_zone: "",
          appointment_notes: "",
          first_consultation_free: false,
          appointment_status: "active",
          appointment_type: "",
          lawyerPhoto: lawyer_image,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="appt-settingsContainer">
      {/* Sidebar */}
      <Box className="appt-settingsSidebar">
        <NarrowInputField
          //   name="Appointment Name"
          //   title="Appointment Name"
          placeholder="Find attorney..."
          isRequired={false}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          halfWidth={false}
        />

        <div style={{ height: "30px" }} />

        <Box component="ul" className="appt-providerList">
          {isLoading ? (
            <li>Loading...</li>
          ) : (
            filteredProfessionals.map((p) => (
              <li
                key={p.id}
                className={
                  "appt-providerItem" +
                  (selectedProfessional?.id === p.id ? " selected" : "")
                }
                onClick={() => setSelectedProfessional(p)}
              >
                {p.name}
              </li>
            ))
          )}
        </Box>
      </Box>

      {/* Main Panel */}
      <div className="appt-settingsMain">
        {firmStripeStatus === false ? (
          <div className="appt-payment-banner">
            <img src={SetupPayment} alt="Setup Payment" />
            <p>Please complete banking setup to enable services.</p>
          </div>
        ) : (
          <div>
            {!isAdding ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                  }}
                >
                  {selectedProfessional && (
                    <h1 className="appt-types-page-title">
                      Appointment types for {selectedProfessional.name}
                    </h1>
                  )}
                  {/* Only you (current user) can add types */}
                  {selectedProfessional?.user_id === userId && (
                    <button
                      className="appt-new-appt-btn"
                      onClick={() => setIsAdding(true)}
                    >
                      New Appointment Type
                    </button>
                  )}
                </div>

                <p className="appt-types-page-content">
                  Review or customize the types of legal consultations you
                  offer. Clients will be able to book appointments based on the
                  services you provide and your availability.
                </p>

                <div className="appt-appointments-grid">
                  {loadingAppointments ? (
                    <p>Loading appointment types...</p>
                  ) : appointmentTypes.length > 0 ? (
                    appointmentTypes.map((appt, i) => (
                      <AppointmentCard
                        key={i}
                        appointment={appt}
                        onMenuClick={handleMenuClick}
                      />
                    ))
                  ) : (
                    <p className="appt-card-title">
                      No appointment types found.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              // ——— ADD FORM VIEW ———
              <div className="appt-addForm">
                <button
                  className="appt-back-link"
                  onClick={() => setIsAdding(false)}
                >
                  <img
                    src={BackIcon}
                    alt="Back"
                    style={{
                      height: "20px",
                      width: "20px",
                      marginRight: "8px",
                    }}
                  />
                  Back to {selectedProfessional?.name}'s appointment type
                </button>
                <h1 className="appt-types-page-title">
                  Add new appointment type
                </h1>
                <div style={{ height: "2rem" }} />

                <NarrowInputField
                  name="appointment_name"
                  title="Appointment Name"
                  placeholder="e.g. Initial Consultation"
                  value={newAppointment.appointment_name}
                  onChange={handleInputChange}
                  halfWidth={false}
                />

                <NarrowDropdown2
                  title="Duration"
                  placeholder="Select appointment duration"
                  items={duration}
                  value={newAppointment.appointment_duration}
                  onChange={(val) =>
                    setNewAppointment({
                      ...newAppointment,
                      appointment_duration: val,
                    })
                  }
                  halfWidth={false}
                />

                <NarrowInputField
                  name="appointment_price" // ← must match your state key
                  title="Price"
                  placeholder="e.g. 100"
                  isRequired
                  value={newAppointment.appointment_price}
                  onChange={handleInputChange}
                />
                <TextAreaWithCount
                  name="appointment_description"
                  title="Appointment description (optional)"
                  placeholder="Describe the services offered during this consultation."
                  value={newAppointment.appointment_description}
                  onChange={handleInputChange}
                />

                <hr className="appt-divider" />
                <div style={{ height: "2rem" }} />

                <h1 className="appt-types-page-subtitle">
                  Attach intake form{" "}
                  <span className="optional-text">(Optional)</span>{" "}
                </h1>
                <div style={{ height: "1rem" }} />

                <NarrowDropdown2
                  title="Select intake form"
                  placeholder="None"
                  items={intakeForms.map((f) => ({
                    label: f.name,
                    value: f.id,
                  }))}
                  value={selectedIntakeForm}
                  onChange={setSelectedIntakeForm}
                  multiple={false}
                  halfWidth={false}
                />

                {/* <p
                  className="optional-text"
                  style={{ marginTop: "1rem", marginBottom: "1rem" }}
                >
                  or
                </p> */}

                {/* <div className="intake-row">
                  <button className="intake-btn">
                    <img
                      src={UpIcon}
                      alt="Upload Icon"
                      style={{ height: "24px", width: "24px" }}
                    />
                    Upload Intake
                  </button>
                  <button
                    className="intake-btn"
                    onClick={() =>
                      navigate("/attorney/intake/form-builder", {
                        state: {
                          from: "appointmentTypeCreation",
                          appointmentDraft: newAppointment,
                          onFormCreatedReturnTo: "/attorney/settings?tab=appointment-type",
                        },
                      })
                    }
                  >
                    <img
                      src={UpIcon}
                      alt="Add Icon"
                      style={{ height: "24px", width: "24px" }}
                    />
                    Use Form Builder
                  </button>
                  <button className="intake-btn">
                    <img
                      src={AIIcon}
                      alt="AI Icon"
                      style={{ height: "24px", width: "24px" }}
                    />
                    Generate with AI
                  </button>
                </div>
                <hr className="appt-divider" /> */}
                <div style={{ height: "2rem" }} />

                <NarrowDropdown2
                  title="Cancellation Policy"
                  placeholder="Select how many hours in advance"
                  items={policy}
                  value={newAppointment.appointment_type}
                  onChange={(val) =>
                    setNewAppointment({
                      ...newAppointment,
                      appointment_type: val,
                    })
                  }
                  halfWidth={false}
                />

                <div className="appt-toggle-area">
                  <span className="appt-label-switch">
                    First consultation free
                  </span>
                  <CustomSwitch
                    checked={newAppointment.first_consultation_free}
                    onChange={(e) =>
                      setNewAppointment({
                        ...newAppointment,
                        first_consultation_free: e.target.checked,
                      })
                    }
                  />
                </div>

                <hr className="appt-divider" />
              </div>
            )}
          </div>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem>Edit</MenuItem>
          <MenuItem>Clone</MenuItem>
          <MenuItem>Delete</MenuItem>
          <hr className="appt-menuDivider" />
          <MenuItem>
            Turn On/Off <input type="checkbox" style={{ marginLeft: "10px" }} />
          </MenuItem>
        </Menu>
        {isAdding ? (
          <div
            style={{
              marginTop: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="help-text">Need help?</p>

            <div className="appt-formActions">
              <button className="appt-cancel-btn" onClick={handleCancelPressed}>
                Cancel
              </button>
              <button
                className="appt-confirm-btn"
                onClick={handleCreateAppointment}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <></>
        )}
        {showUploadForm && (
          <UploadForm
            open={showUploadForm}
            onClose={() => setShowUploadForm(false)}
            onNext={(parsed_json) => {
              setShowUploadForm(false);
              // Assume parsed_json contains id and name for the new form
              navigate("/attorney/settings?tab=appointment-type", {
                state: {
                  newFormId: parsed_json.id,
                  newFormName: parsed_json.name,
                  appointmentDraft: newAppointment,
                },
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SettingsAppointmentTypes;
